import { useEffect, useRef } from 'react';

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: unknown) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  start: () => void;
  stop: () => void;
}

interface Props {
  /** When false, no recognition session exists at all (e.g. mock mode). */
  enabled: boolean;
  /** When true, the interviewer is speaking — candidate results are suppressed. */
  paused: boolean;
  onResult: (text: string, isFinal: boolean) => void;
}

const SUPPRESS_AFTER_AI_MS = 700;

/**
 * Captures the candidate's spoken answers through the browser's built-in
 * speech recognition (Chrome/Edge). Gemini's live input transcription is
 * unreliable, so the candidate's own words — and therefore a reliable
 * "answered" counter and a transcript the evaluator can actually score —
 * come from here instead. Nothing is emitted while the interviewer speaks,
 * minus a short echo-dead window.
 */
export function useLocalSpeechRecognition({ enabled, paused, onResult }: Props): void {
  const enabledRef = useRef(enabled);
  const onResultRef = useRef(onResult);
  const pausedRef = useRef(false);
  const suppressUntilRef = useRef(0);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const restoringRef = useRef(false);
  const aliveRef = useRef(false);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    if (paused) {
      if (!pausedRef.current) {
        pausedRef.current = true;
        suppressUntilRef.current = Date.now() + SUPPRESS_AFTER_AI_MS;
        // Hard-stop the pending recognition session. Chrome continuously
        // transcribes the interviewer's speech heard through the speakers, then
        // flushes that buffered audio into the first candidate final once the
        // interviewer goes quiet — merging the question into the answer line.
        // stop() discards that buffer; the onend/onerror handlers below refuse
        // to restart while paused, so the recognizer stays dead until the
        // interviewer finishes and this hook starts it again.
        const rec = recRef.current;
        if (rec) {
          try {
            rec.stop();
          } catch {
            // not running
          }
        }
      }
    } else if (pausedRef.current) {
      // Interviewer finished speaking: start a brand-new recognition session so
      // no buffered interviewer audio can surface as a candidate line.
      pausedRef.current = false;
      // Suppress from the RESUME point (not the pause point): the interviewer's
      // tail syllables are acoustically alive right now, so any result within
      // the next window is almost certainly echo of its own voice.
      suppressUntilRef.current = Date.now() + SUPPRESS_AFTER_AI_MS;
      const rec = recRef.current;
      if (rec && enabledRef.current && !aliveRef.current) {
        try {
          rec.start();
          aliveRef.current = true;
        } catch {
          // may already be starting
        }
      }
    } else {
      pausedRef.current = false;
    }
  }, [paused]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const win = window as unknown as {
      SpeechRecognition?: new () => SpeechRecognitionLike;
      webkitSpeechRecognition?: new () => SpeechRecognitionLike;
    };
    const Ctor = win.SpeechRecognition ?? win.webkitSpeechRecognition;
    if (!Ctor) {
      return;
    }

    const newRec = new Ctor();
    newRec.lang = 'en-US';
    newRec.continuous = true;
    newRec.interimResults = true;
    newRec.onresult = (event: unknown) => {
      const evt = event as {
        resultIndex: number;
        results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
      };
      if (Date.now() < suppressUntilRef.current || pausedRef.current) {
        return;
      }
      const last = evt.results[evt.results.length - 1];
      if (!last) {
        return;
      }
      const text = last[0].transcript.trim();
      if (!text) {
        return;
      }
      onResultRef.current(text, last.isFinal);
    };
    newRec.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        recRef.current = null;
        aliveRef.current = false;
      }
      if (
        (event.error === 'no-speech' || event.error === 'aborted' || event.error === 'network') &&
        !pausedRef.current
      ) {
        restoringRef.current = true;
        if (enabledRef.current) {
          try {
            newRec.start();
            aliveRef.current = true;
          } catch {
            // already started
          }
        }
      }
    };
    newRec.onend = () => {
      aliveRef.current = false;
      if (enabledRef.current && recRef.current && !pausedRef.current) {
        if (restoringRef.current) {
          restoringRef.current = false;
          return;
        }
        setTimeout(() => {
          if (enabledRef.current && recRef.current && !pausedRef.current) {
            try {
              newRec.start();
              aliveRef.current = true;
            } catch {
              // already started
            }
          }
        }, 300);
      }
    };
    recRef.current = newRec;
    try {
      newRec.start();
      aliveRef.current = true;
    } catch {
      // already started
    }

    return () => {
      recRef.current = null;
      aliveRef.current = false;
      newRec.onresult = null;
      newRec.onend = null;
      newRec.onerror = null;
      try {
        newRec.stop();
      } catch {
        // not running
      }
    };
  }, [enabled]);
}